import React from 'react';
import { useData, useField } from '../EpcrDataContext';
import { Icons } from '../EpcrIcons';
import { ViewLayout } from '../EpcrFields';

export function EpcrViewFlowchart() {
  const data = useData();
  const procedures = useField("eProcedures/eProcedures.ProcedureGroup") || [];
  const medsRaw = useField("eMedications/eMedications.MedicationGroup");
  const meds = Array.isArray(medsRaw) ? medsRaw : (medsRaw ? [medsRaw] : []);
  const devices = useField("eDevice/eDevice.DeviceGroup") || [];

  const events = React.useMemo(() => {
    const all = [];
    (Array.isArray(procedures) ? procedures : []).forEach((p, idx) => {
      all.push({
        time: p["eProcedures.01"],
        kind: "proc",
        label: "Procedure",
        title: data.label("eProcedures.03", p["eProcedures.03"]) || p["eProcedures.03"],
        sub: `Attempt ${p["eProcedures.05"] || 1} · ${data.label("eProcedures.06", p["eProcedures.06"]) || "—"}`,
        meta: data.label("eProcedures.07", p["eProcedures.07"]),
        groupPrefix: "eProcedures",
        groupIndex: idx,
      });
    });
    meds.forEach((m, idx) => {
      all.push({
        time: m["eMedications.01"],
        kind: "med",
        label: "Medication",
        title: data.label("eMedications.03", m["eMedications.03"]) || m["eMedications.03"],
        sub: `${m["eMedications.DosageGroup"]?.["eMedications.05"] || ""} ${data.label("eMedications.06", m["eMedications.DosageGroup"]?.["eMedications.06"]) || ""} · ${data.label("eMedications.07", m["eMedications.07"]) || ""}`,
        meta: data.label("eMedications.08", m["eMedications.08"]),
        groupPrefix: "eMedications",
        groupIndex: idx,
      });
    });
    (Array.isArray(devices) ? devices : []).forEach((d, idx) => {
      all.push({
        time: d["eDevice.02"],
        kind: "device",
        label: "Device",
        title: data.label("eDevice.03", d["eDevice.03"]) || "Cardiac monitor event",
        sub: `Serial ${d["eDevice.01"]}`,
        meta: d["eDevice.WaveformGroup"]?.["eDevice.04"] ? "Waveform attached" : "",
        groupPrefix: "eDevice",
        groupIndex: idx,
      });
    });
    return all.sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  }, [procedures, meds, devices, data]);

  const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toTimeString().slice(0, 5);
  };

  const sections = events.map((e, i) => ({
    id: `flow-${i}`,
    label: `${fmtTime(e.time)} · ${e.title}`,
  }));

  const addBar = (
    <div className="add-bar" style={{ flexWrap: "wrap" }}>
      <button className="btn-secondary"><Icons.plus size={14} /> Airway</button>
      <button className="btn-secondary"><Icons.plus size={14} /> Critical care</button>
      <button className="btn-secondary"><Icons.plus size={14} /> Defib / Cardio</button>
      <button className="btn-secondary"><Icons.plus size={14} /> IV therapy</button>
      <button className="btn-primary"><Icons.plus size={14} /> Medication</button>
      <button className="btn-secondary"><Icons.plus size={14} /> Other</button>
    </div>
  );

  return (
    <ViewLayout sections={sections} addBar={addBar}>
      <div className="tl">
        {events.map((e, i) => (
          <div className="tl-item" key={i} id={`flow-${i}`} data-section={`flow-${i}`}
               data-group-prefix={e.groupPrefix} data-group-index={e.groupIndex}
               style={{ scrollMarginTop: 20 }}>
            <div className="tl-time">
              {fmtTime(e.time)}
              <span className="ms">{i === 0 ? "T+0" : `+${diffMin(events[0].time, e.time)}`}</span>
            </div>
            <div className={"tl-dot " + (e.kind === "med" ? "blue" : e.kind === "device" ? "red" : "green")} />
            <div className="tl-card">
              <span className={"kind " + (e.kind === "med" ? "med" : e.kind === "proc" ? "proc" : "airway")}>{e.label}</span>
              <div>
                <div className="title">{e.title}</div>
                {e.sub && <div className="sub">{e.sub}</div>}
              </div>
              <div className="meta">{e.meta || ""}</div>
            </div>
          </div>
        ))}
      </div>
    </ViewLayout>
  );
}

function diffMin(a, b) {
  if (!a || !b) return "—";
  const ms = new Date(b) - new Date(a);
  return Math.round(ms / 60000) + "m";
}
